(() => {
  if (window.__vlBarcodeScannerInit) return;
  window.__vlBarcodeScannerInit = true;

  const dialog = document.getElementById('barcode-scanner-dialog');
  const trigger = document.querySelector('[data-barcode-scan-trigger]');
  if (!dialog || !trigger) return;

  const video = dialog.querySelector('[data-barcode-video]');
  const status = dialog.querySelector('[data-barcode-status]');
  const startButton = dialog.querySelector('[data-barcode-start]');
  const stopButton = dialog.querySelector('[data-barcode-stop]');
  const closeButton = dialog.querySelector('[data-barcode-scan-close]');
  const manualInput = dialog.querySelector('[data-barcode-manual-input]');
  const manualSubmit = dialog.querySelector('[data-barcode-manual-submit]');

  let stream = null;
  let detector = null;
  let rafId = null;
  let zxingReader = null;
  let scannerActive = false;
  let hasNavigated = false;
  let zxingLoaded = false;
  const preferredFormats = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'itf'];
  let polyfillReady = false;

  const isIOS = () => {
    const ua = window.navigator.userAgent || '';
    const platform = window.navigator.platform || '';
    const touchPoints = window.navigator.maxTouchPoints || 0;
    return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && touchPoints > 1);
  };

  const emitEvent = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...detail });
    }
  };

  const setStatus = (message) => {
    if (status) status.textContent = message || '';
  };

  const stopStream = () => {
    scannerActive = false;
    if (rafId) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
    if (zxingReader?.reset) {
      zxingReader.reset();
    }
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      stream = null;
    }
    if (video) video.srcObject = null;
  };

  const resolveBarcodeToUrl = async (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return null;

    const url = `/search/suggest.json?q=${encodeURIComponent(
      code
    )}&resources[type]=product&resources[limit]=8`;
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return null;

    const payload = await response.json();
    const products = payload?.resources?.results?.products || [];
    if (!products.length) return null;

    const exactMatch = products.find((product) => {
      const searchable = `${product.title || ''} ${product.body || ''} ${product.vendor || ''}`.toLowerCase();
      return searchable.includes(code.toLowerCase());
    });
    return (exactMatch || products[0]).url || null;
  };

  const onBarcodeFound = async (rawCode) => {
    if (!rawCode || hasNavigated) return;
    hasNavigated = true;
    stopStream();

    setStatus(`Scanned: ${rawCode}`);
    emitEvent('barcode_scan_success', { barcode: rawCode });

    try {
      const matchUrl = await resolveBarcodeToUrl(rawCode);
      if (matchUrl) {
        window.location.assign(matchUrl);
        return;
      }

      emitEvent('barcode_scan_no_match', { barcode: rawCode });
      setStatus('No matching product found. Showing search results.');
      window.location.assign(`/search?q=${encodeURIComponent(rawCode)}&type=product`);
    } catch (error) {
      console.error('Barcode lookup failed', error);
      emitEvent('barcode_scan_error', { reason: 'lookup_failed' });
      setStatus('Something went wrong while looking up that barcode.');
    }
  };

  const loadZxing = async () => {
    if (zxingLoaded && window.ZXing?.BrowserMultiFormatReader) return true;

    await new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-zxing-loader]');
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@zxing/library@0.21.3/umd/index.min.js';
      script.defer = true;
      script.dataset.zxingLoader = 'true';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });

    zxingLoaded = !!window.ZXing?.BrowserMultiFormatReader;
    return zxingLoaded;
  };

  const loadBarcodeDetectorPolyfill = async () => {
    if (polyfillReady) return true;

    try {
      const nativeDetector = window.BarcodeDetector;
      if (!nativeDetector && isIOS()) {
        const module = await import(
          'https://cdn.jsdelivr.net/npm/@undecaf/barcode-detector-polyfill@0.9.23/dist/main.js'
        );
        if (module?.BarcodeDetectorPolyfill) {
          window.BarcodeDetector = module.BarcodeDetectorPolyfill;
        }
      }
      polyfillReady = !!window.BarcodeDetector;
      return polyfillReady;
    } catch (error) {
      console.warn('WASM BarcodeDetector polyfill failed to load', error);
      return false;
    }
  };

  const startBarcodeDetectorLoop = () => {
    const loop = async () => {
      if (!scannerActive || !detector || !video) return;

      try {
        const barcodes = await detector.detect(video);
        if (barcodes?.length) {
          const rawValue = barcodes[0]?.rawValue;
          if (rawValue) {
            onBarcodeFound(rawValue);
            return;
          }
        }
      } catch (error) {
        console.error('BarcodeDetector error', error);
      }

      rafId = window.requestAnimationFrame(loop);
    };

    rafId = window.requestAnimationFrame(loop);
  };

  const startZxingFallback = async () => {
    const loaded = await loadZxing();
    if (!loaded) {
      setStatus('Scanner fallback could not load.');
      emitEvent('barcode_scan_error', { reason: 'fallback_load_failed' });
      return;
    }

    zxingReader = new window.ZXing.BrowserMultiFormatReader();
    setStatus('Point your camera at a barcode.');

    zxingReader.decodeFromVideoElementContinuously(video, (result, err) => {
      if (result?.text) {
        onBarcodeFound(result.text);
      } else if (err) {
        const errorName = err?.name || '';
        const errorMessage = err?.message || '';
        const isExpectedNoMatch =
          errorName === 'NotFoundException' ||
          /No MultiFormat Readers were able to detect the code/i.test(errorMessage);

        // This is the normal polling state while no barcode is currently visible.
        if (isExpectedNoMatch) return;

        console.error('ZXing decode error', err);
      }
    });
  };

  const startScanner = async () => {
    if (scannerActive) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      setStatus('Camera is not available on this device.');
      emitEvent('barcode_scan_error', { reason: 'media_devices_missing' });
      return;
    }

    hasNavigated = false;
    setStatus('Requesting camera access...');

    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: 'environment' } },
      });
      video.srcObject = stream;
      await video.play();
      scannerActive = true;
      emitEvent('barcode_scan_start');

      // On iOS, prefer a WASM-backed BarcodeDetector polyfill for reliable 1D scans.
      if (!window.BarcodeDetector && isIOS()) {
        await loadBarcodeDetectorPolyfill();
      }

      if (window.BarcodeDetector) {
        try {
          let supportedFormats = preferredFormats;
          if (typeof window.BarcodeDetector.getSupportedFormats === 'function') {
            const nativeFormats = await window.BarcodeDetector.getSupportedFormats();
            const formatSet = new Set(nativeFormats || []);
            supportedFormats = preferredFormats.filter((format) => formatSet.has(format));
          }

          // iOS may expose BarcodeDetector but not support common 1D formats reliably.
          // If no preferred retail formats are available, skip native path and use ZXing.
          if (supportedFormats.length > 0) {
            detector = new window.BarcodeDetector({ formats: supportedFormats });
            setStatus('Point your camera at a barcode.');
            startBarcodeDetectorLoop();
            return;
          }
        } catch (error) {
          console.warn('BarcodeDetector setup failed, falling back to ZXing', error);
        }
      }

      setStatus('Using iOS fallback scanner...');
      await startZxingFallback();
    } catch (error) {
      console.error('Unable to start scanner', error);
      setStatus('Camera access denied. Please allow camera access and try again.');
      emitEvent('barcode_scan_error', { reason: 'camera_denied' });
      stopStream();
    }
  };

  const openDialog = async () => {
    if (!dialog.open) dialog.showModal();
    emitEvent('barcode_scan_open');
    await startScanner();
  };

  const closeDialog = () => {
    stopStream();
    if (dialog.open) dialog.close();
    emitEvent('barcode_scan_close');
  };

  trigger.addEventListener('click', openDialog);
  closeButton?.addEventListener('click', closeDialog);
  stopButton?.addEventListener('click', closeDialog);
  startButton?.addEventListener('click', startScanner);

  dialog.addEventListener('close', stopStream);
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });

  manualSubmit?.addEventListener('click', () => {
    const manualValue = manualInput?.value?.trim();
    if (!manualValue) return;
    emitEvent('barcode_scan_manual_submit', { barcode: manualValue });
    onBarcodeFound(manualValue);
  });

  manualInput?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      manualSubmit?.click();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stopStream();
  });
})();
